import { ChangeDetectionStrategy, Component, computed, input, output, Signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { provideTranslocoScope, TranslocoDirective } from '@jsverse/transloco';
import type { AvailableProfile, ProfileIdentifier } from '@rma/authentication/public/user';
import { AvatarComponent } from '@rma/generic/ui/avatar';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { TypeAheadHighlightPipe } from '@rma/generic/util/pipes/type-ahead-highlight';
import { TRANSLATION_SCOPE } from '../domain/translation-scope';

type PrimaryAction = 'manage' | 'none' | 'stop' | 'switch';

@Component({
  selector: 'rma-profile-switch-item',
  standalone: true,
  imports: [AvatarComponent, MatButtonModule, MatTooltipModule, RmaIconComponent, TranslocoDirective, TypeAheadHighlightPipe],
  providers: [provideTranslocoScope(TRANSLATION_SCOPE)],
  templateUrl: './profile-switch-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex',
  },
})
export class ProfileSwitchItemComponent {
  public readonly activeProfile = input.required<ProfileIdentifier>();
  public readonly defaultProfile = input.required<ProfileIdentifier>();
  public readonly isAssisting = input.required<boolean>();
  public readonly profile = input.required<AvailableProfile>();

  public readonly searchTerm = input<string>('');

  public readonly manageAccount = output<{ profile: AvailableProfile; agentName?: string }>();
  public readonly stopAssisting = output();
  public readonly switchProfile = output<AvailableProfile>();

  public readonly parentProfile = computed(() => this.profile().parentProfiles?.[0]);
  public readonly parentNames = computed(() => this.joinNames(this.profile()));

  public readonly isActive = computed(() => this.profilesMatch(this.activeProfile(), this.profile()));
  public readonly isSelf = computed(() => this.profilesMatch(this.defaultProfile(), this.profile()));

  public readonly isIndividual = computed(() => this.profile().profileType === 'Individual');
  public readonly hasAdminAccess = computed(() => Boolean(this.profile().adminToAgentPermission?.includes('Core')));
  public readonly noAdminAccess = computed(() => this.isIndividual() && !this.hasAdminAccess());

  public readonly primaryAction: Signal<PrimaryAction> = computed(() =>
    this.getPrimaryAction(this.isActive(), this.isSelf(), this.isAssisting(), !this.noAdminAccess(), Boolean(this.parentProfile())),
  );

  public readonly scope = TRANSLATION_SCOPE;

  public onPrimaryActionClick(action: PrimaryAction) {
    switch (action) {
      case 'manage':
        this.onManageAccess(this.parentProfile(), this.profile().name);
        break;
      case 'stop':
        this.stopAssisting.emit();
        break;
      case 'switch':
        this.switchProfile.emit(this.profile());
        break;
    }
  }

  public onStopAssisting(): void {
    this.stopAssisting.emit();
  }

  public onManageAccess(profile?: AvailableProfile, agentName?: string): void {
    if (profile) {
      return this.manageAccount.emit({ profile, agentName });
    }
  }

  private getPrimaryAction(
    isActive: boolean,
    isSelf: boolean,
    isAssisting: boolean,
    hasAdminAccess: boolean,
    hasParentProfile: boolean,
  ): PrimaryAction {
    const isStop = isActive && isAssisting;
    const isSwitch = !isActive && (hasAdminAccess || isSelf);
    const isRequestAccess = !isActive && !hasAdminAccess && hasParentProfile;

    if (isStop) {
      return 'stop';
    }
    if (isSwitch) {
      return 'switch';
    }
    if (isRequestAccess) {
      return 'manage';
    }
    return 'none';
  }

  private joinNames(profile: AvailableProfile): string {
    return profile.parentProfiles?.map((item) => `${item.name}`).join(', ') ?? '';
  }

  private profilesMatch(testProfile: ProfileIdentifier, baseProfile: AvailableProfile): boolean {
    return testProfile.profileCode === baseProfile.profileCode && testProfile.profileType === baseProfile.profileType;
  }
}
